import React, { useState } from 'react';
import { Card, Select, Input, DatePicker, Button, Space, Form, InputNumber } from 'antd';
import { DeleteOutlined, FilterOutlined } from '@ant-design/icons';
import moment from 'moment';

const { Option } = Select;
const { RangePicker } = DatePicker;

const FilterCard = ({ filter, onUpdate, onRemove }) => {
  const [form] = Form.useForm();

  // Use operators from filter metadata if available, otherwise fallback to defaults
  const getOperatorsForType = (type) => {
    if (filter.field?.operators) {
      return filter.field.operators.map(op => ({
        value: op,
        label: op.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())
      }));
    }

    // Fallback operators
    const fallbackOperators = {
      string: [
        { value: 'equals', label: 'Equals' },
        { value: 'notEquals', label: 'Not Equals' },
        { value: 'contains', label: 'Contains' },
        { value: 'startsWith', label: 'Starts With' },
        { value: 'endsWith', label: 'Ends With' },
        { value: 'isNull', label: 'Is Empty' },
        { value: 'isNotNull', label: 'Is Not Empty' }
      ],
      number: [
        { value: 'equals', label: 'Equals' },
        { value: 'notEquals', label: 'Not Equals' },
        { value: 'greaterThan', label: 'Greater Than' },
        { value: 'greaterThanOrEqual', label: 'Greater Than or Equal' },
        { value: 'lessThan', label: 'Less Than' },
        { value: 'lessThanOrEqual', label: 'Less Than or Equal' },
        { value: 'between', label: 'Between' },
        { value: 'isNull', label: 'Is Empty' },
        { value: 'isNotNull', label: 'Is Not Empty' }
      ],
      date: [
        { value: 'equals', label: 'Equals' },
        { value: 'notEquals', label: 'Not Equals' },
        { value: 'greaterThan', label: 'After' },
        { value: 'greaterThanOrEqual', label: 'On or After' },
        { value: 'lessThan', label: 'Before' },
        { value: 'lessThanOrEqual', label: 'On or Before' },
        { value: 'between', label: 'Between' },
        { value: 'isNull', label: 'Is Empty' },
        { value: 'isNotNull', label: 'Is Not Empty' }
      ],
      boolean: [
        { value: 'equals', label: 'Is' },
        { value: 'notEquals', label: 'Is Not' }
      ]
    };

    return fallbackOperators[type] || fallbackOperators.string;
  };

  const handleOperatorChange = (operator) => {
    const updatedFilter = { ...filter, operator, value: undefined, valueTo: undefined };
    onUpdate(updatedFilter);
  };

  const handleValueChange = (value, field = 'value') => {
    const updatedFilter = { ...filter, [field]: value };
    onUpdate(updatedFilter);
  };

  const renderValueInput = () => {
    const { operator, type } = filter;

    // For null checks, no value input needed
    if (operator === 'isNull' || operator === 'isNotNull') {
      return null;
    }

    switch (type) {
      case 'string':
        return (
          <Input
            placeholder="Enter value"
            value={filter.value || ''}
            onChange={(e) => handleValueChange(e.target.value)}
            style={{ width: '100%' }}
          />
        );

      case 'number':
        if (operator === 'between') {
          return (
            <Space>
              <InputNumber
                placeholder="Min value"
                value={filter.value}
                onChange={(value) => handleValueChange(value)}
                style={{ width: '120px' }}
              />
              <span>to</span>
              <InputNumber
                placeholder="Max value"
                value={filter.valueTo}
                onChange={(value) => handleValueChange(value, 'valueTo')}
                style={{ width: '120px' }}
              />
            </Space>
          );
        }
        return (
          <InputNumber
            placeholder="Enter number"
            value={filter.value}
            onChange={(value) => handleValueChange(value)}
            style={{ width: '100%' }}
          />
        );

      case 'date':
        if (operator === 'between') {
          console.log('Rendering between date picker:', { value: filter.value, valueTo: filter.valueTo });
          return (
            <RangePicker
              value={
                (filter.value || filter.valueTo)
                  ? [
                      filter.value ? moment(filter.value) : null,
                      filter.valueTo ? moment(filter.valueTo) : null
                    ]
                  : [null, null]
              }
              onChange={(dates, dateStrings) => {
                console.log('RangePicker onChange:', { dates, dateStrings });
                if (dates && dates.length === 2) {
                  // Update both values in a single state update
                  const updatedFilter = {
                    ...filter,
                    value: dateStrings[0] || undefined,
                    valueTo: dateStrings[1] || undefined
                  };
                  onUpdate(updatedFilter);
                } else if (dates === null || dates.length === 0) {
                  // Clear both values
                  const updatedFilter = {
                    ...filter,
                    value: undefined,
                    valueTo: undefined
                  };
                  onUpdate(updatedFilter);
                } else if (dates.length === 1) {
                  // Handle partial selection (only one date selected)
                  const updatedFilter = {
                    ...filter,
                    value: dateStrings[0] || undefined,
                    valueTo: filter.valueTo // Keep existing valueTo if it exists
                  };
                  onUpdate(updatedFilter);
                }
              }}
              style={{ width: '100%' }}
              allowEmpty={[true, true]}
              placeholder={['Start date', 'End date']}
            />
          );
        }
        return (
          <DatePicker
            value={filter.value ? moment(filter.value) : null}
            onChange={(date, dateString) => handleValueChange(dateString || undefined)}
            style={{ width: '100%' }}
          />
        );

      case 'boolean':
        return (
          <Select
            value={filter.value}
            onChange={(value) => handleValueChange(value)}
            style={{ width: '100%' }}
          >
            <Option value={true}>True</Option>
            <Option value={false}>False</Option>
          </Select>
        );

      default:
        return (
          <Input
            placeholder="Enter value"
            value={filter.value || ''}
            onChange={(e) => handleValueChange(e.target.value)}
            style={{ width: '100%' }}
          />
        );
    }
  };

  return (
    <Card
      size="small"
      style={{
        border: '1px solid #b7eb8f',
        backgroundColor: '#f6ffed'
      }}
      bodyStyle={{ padding: '12px' }}
    >
      <Space direction="vertical" size="small" style={{ width: '100%' }}>
        <Space align="center" style={{ width: '100%', justifyContent: 'space-between' }}>
          <Space align="center">
            <FilterOutlined style={{ color: '#52c41a' }} />
            <span style={{ fontWeight: 'bold', color: '#389e0d' }}>
              {filter.field?.label || 'Filter'}
            </span>
          </Space>
          <Button
            type="text"
            size="small"
            icon={<DeleteOutlined />}
            onClick={onRemove}
            danger
            style={{ padding: '2px' }}
          />
        </Space>

        <Space wrap size="small" style={{ width: '100%' }}>
          <Select
            value={filter.operator}
            onChange={handleOperatorChange}
            style={{ minWidth: '140px' }}
            placeholder="Select operator"
          >
            {getOperatorsForType(filter.type).map(op => (
              <Option key={op.value} value={op.value}>
                {op.label}
              </Option>
            ))}
          </Select>

          {renderValueInput()}
        </Space>
      </Space>
    </Card>
  );
};

export default FilterCard;